import React, { useState, useRef, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Card as CardType, CardColor } from '../../types';
import CardContainer from './CardContainer';
import CardHeader from './CardHeader';
import CardContent from './CardContent';
import TagsContainer from './TagsContainer';
import CardFooter from './CardFooter';
import Divider from '../common/Divider';
import ResizeHandle from './ResizeHandle';

// デフォルトのカードサイズ
const DEFAULT_CARD_WIDTH = 250;
const DEFAULT_CARD_HEIGHT = 200;

interface CardProps {
  card: CardType;
  onContentChange: (id: string, content: string) => void;
  onColorChange: (id: string, color: CardColor) => void;
  onTagAdd: (id: string, tag: string) => void;
  onTagRemove: (id: string, tagId: string) => void;
  onDelete: (id: string) => void;
  onResize?: (id: string, width: number, height: number) => void;
  onBringToFront?: (id: string) => void;
  isNewlyAdded?: boolean;
}

const Card: React.FC<CardProps> = ({
  card,
  onContentChange,
  onColorChange,
  onTagAdd,
  onTagRemove,
  onDelete,
  onResize,
  onBringToFront,
  isNewlyAdded,
}) => {
  // リサイズ関連のstate
  const [isResizing, setIsResizing] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  
  // カードのDOM参照
  const cardRef = useRef<HTMLDivElement>(null);
  // テキストエリアの参照
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // ドラッグ機能の設定
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: card.id,
    data: {
      card,
    },
    disabled: isResizing, // リサイズ中はドラッグを無効化
  });

  // テキスト内容変更時のハンドラ
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onContentChange(card.id, e.target.value);
    
    // テキスト入力時も最前面に持ってくる
    if (onBringToFront) {
      onBringToFront(card.id);
    }
  };

  // 色選択時のハンドラ
  const handleColorSelect = (color: CardColor) => {
    onColorChange(card.id, color);
    
    // 色を変更したときも最前面に持ってくる
    if (onBringToFront) {
      onBringToFront(card.id);
    }
  };

  // カード全体のクリックハンドラ
  const handleCardClick = () => {
    // カードを最前面に持ってくる
    if (onBringToFront) {
      onBringToFront(card.id);
    }
  };

  // テキストエリアのクリックイベントの伝播を停止
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // テキストエリアをクリックしても最前面に持ってくる
    if (onBringToFront) {
      onBringToFront(card.id);
    }
  };

  // カード削除時のハンドラ
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('このカードを削除してもよろしいですか？')) {
      onDelete(card.id);
    }
  };

  // カードサイズをリセットするハンドラ
  const handleResetSize = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onResize) {
      onResize(card.id, DEFAULT_CARD_WIDTH, DEFAULT_CARD_HEIGHT);
      
      // サイズをリセットしたときも最前面に持ってくる
      if (onBringToFront) {
        onBringToFront(card.id);
      }
    }
  };

  // タグ追加時のハンドラ
  const handleTagAdd = (tag: string) => {
    onTagAdd(card.id, tag);
    
    // タグを追加したときも最前面に持ってくる
    if (onBringToFront) {
      onBringToFront(card.id);
    }
  };

  // タグ削除時のハンドラ
  const handleTagRemove = (tag: string) => {
    onTagRemove(card.id, tag);
    
    // タグを削除したときも最前面に持ってくる
    if (onBringToFront) {
      onBringToFront(card.id);
    }
  };
  
  // リサイズ開始時の処理
  const handleResizeStart = (clientX: number, clientY: number) => {
    if (!cardRef.current) return;
    
    // リサイズ中フラグを設定
    setIsResizing(true);
    
    // 開始時のマウス/タッチ位置を記録
    setStartPoint({ x: clientX, y: clientY });
    
    // 開始時のカードサイズを記録
    const width = card.size?.width || DEFAULT_CARD_WIDTH;
    const height = card.size?.height || DEFAULT_CARD_HEIGHT;
    setStartSize({ width, height });
    
    // グローバルイベントリスナーを設定
    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('touchmove', handleResizeTouchMove, { passive: false });
    window.addEventListener('mouseup', handleResizeEnd);
    window.addEventListener('touchend', handleResizeEnd);
    
    // カードを最前面に持ってくる
    if (onBringToFront) {
      onBringToFront(card.id);
    }
  };
  
  // マウス移動時のリサイズ処理
  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizing) return;
    
    e.preventDefault();
    handleResize(e.clientX, e.clientY);
  };
  
  // タッチ移動時のリサイズ処理
  const handleResizeTouchMove = (e: TouchEvent) => {
    if (!isResizing || e.touches.length === 0) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    handleResize(touch.clientX, touch.clientY);
  };
  
  // 共通のリサイズ処理
  const handleResize = (clientX: number, clientY: number) => {
    if (!cardRef.current) return;
    
    // 移動距離を計算
    const deltaX = clientX - startPoint.x;
    const deltaY = clientY - startPoint.y;
    
    // 新しいサイズを計算（最小サイズを保証）
    const newWidth = Math.max(200, startSize.width + deltaX);
    const newHeight = Math.max(150, startSize.height + deltaY);
    
    // カードのサイズを直接更新
    cardRef.current.style.width = `${newWidth}px`;
    cardRef.current.style.height = `${newHeight}px`;
  };
  
  // リサイズ終了時の処理
  const handleResizeEnd = () => {
    if (!isResizing || !cardRef.current) return;
    
    // リサイズ中フラグをリセット
    setIsResizing(false);
    
    // 最終的なサイズを取得
    const computedStyle = window.getComputedStyle(cardRef.current);
    const width = parseInt(computedStyle.width);
    const height = parseInt(computedStyle.height);
    
    // サイズ変更を親コンポーネントに通知
    if (onResize) {
      onResize(card.id, width, height);
    }
    
    // グローバルイベントリスナーを削除
    window.removeEventListener('mousemove', handleResizeMove);
    window.removeEventListener('touchmove', handleResizeTouchMove);
    window.removeEventListener('mouseup', handleResizeEnd);
    window.removeEventListener('touchend', handleResizeEnd);
  };

  // ドラッグ中のスタイル
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  // カードのサイズ（デフォルト値または指定値）
  const width = card.size?.width || DEFAULT_CARD_WIDTH;
  const height = card.size?.height || DEFAULT_CARD_HEIGHT;

  // カードがデフォルトサイズかどうかを判定
  const isDefaultSize = width === DEFAULT_CARD_WIDTH && height === DEFAULT_CARD_HEIGHT;

  // カードのノード参照を設定
  const setCardNodeRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    cardRef.current = node;
  };

  useEffect(() => {
    if (isNewlyAdded) {
      // 新しいカードが追加されたときにフォーカスする
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [isNewlyAdded]);

  return (
    <CardContainer
      ref={setCardNodeRef}
      style={style}
      color={card.color}
      isDragging={!!transform}
      x={card.position.x}
      y={card.position.y}
      zIndex={card.zIndex}
      width={width}
      height={height}
      attributes={attributes}
      listeners={isResizing ? {} : listeners}
      onClick={handleCardClick}
    >
      <CardHeader 
        onDelete={handleDelete} 
        onResetSize={isDefaultSize ? undefined : handleResetSize}
      />
      <CardContent 
        ref={textareaRef}
        value={card.content} 
        onChange={handleContentChange} 
        onClick={handleContentClick}
        onMouseDown={(e) => e.stopPropagation()}
      />
      <TagsContainer 
        tags={card.tags}
        onTagAdd={handleTagAdd}
        onTagRemove={handleTagRemove}
      />
      <Divider />
      <CardFooter 
        currentColor={card.color}
        onColorSelect={handleColorSelect}
      />
      
      {/* リサイズハンドル（右下のみ） */}
      <ResizeHandle onResizeStart={handleResizeStart} />
    </CardContainer>
  );
};

export default Card; 